import {RepoSelectPanel} from '@github-ui/copilot-chat/components/RepoSelectPanel'
import type {CopilotChatRepo} from '@github-ui/copilot-chat-types'
import type {CopilotSpaceRepositoryResource, CopilotSpaceResource, ResourceCounts} from '../../types'
import {fetchRepo} from '../../utils/fetch-repo'
import {sendEvent} from '@github-ui/hydro-analytics'
import {testIdProps} from '@github-ui/test-id-props'
import {FileCodeIcon, LinkIcon, PasteIcon, RepoIcon, UploadIcon} from '@primer/octicons-react'
import {ActionList, ActionMenu} from '@primer/react'
import {useCallback, useRef, useState} from 'react'

import {FileUploadInput, type FileUploadInputRef} from './FileUploadInput'
import {GitHubUrlDialog} from './GitHubUrlDialog'
import {SpacesFilePicker} from '../SpacesFilePicker'
import {TextFileDialog} from './TextFileDialog'

interface AddAttachmentMenuProps {
  children: React.ReactElement
  onResourcesAdded: (resources: CopilotSpaceResource[]) => void
  onError?: (errorMessages: string[]) => void
  owner: string | undefined
  spaceOwner: string
  sizePercentage?: number
  resourcesToSave: CopilotSpaceResource[]
  resourceCounts?: ResourceCounts
  onUploadingChange?: (uploading: boolean) => void
  initialRepo?: CopilotChatRepo | null
  onRepoSelect?: (repo: CopilotChatRepo | null) => void
}

export const AddAttachmentMenu = ({
  children,
  onResourcesAdded,
  onError,
  owner,
  spaceOwner,
  sizePercentage,
  resourcesToSave,
  resourceCounts,
  onUploadingChange,
  initialRepo,
  onRepoSelect,
}: AddAttachmentMenuProps) => {
  const [showAddTextFileDialog, setShowAddTextFileDialog] = useState(false)
  const [showMultiFilePicker, setShowMultiFilePicker] = useState(false)
  const [showAddGitHubUrlDialog, setShowAddGitHubUrlDialog] = useState(false)
  const [showRepoSelectPanel, setShowRepoSelectPanel] = useState(false)
  const [repoSelectError, setRepoSelectError] = useState<string>()
  const fileUploadInputRef = useRef<FileUploadInputRef>(null)
  const attachmentButtonRef = useRef<HTMLButtonElement>(null)
  // Track when a repository selection is in progress so other panel open/close events triggered by
  // focus changes don’t fire while we’re awaiting the repo fetch. Using a ref avoids a rerender that could
  // cause the RepoSelectPanel to reopen or close mid-selection.
  const selectingRepoRef = useRef(false)

  const openFilePicker = () => {
    sendEvent('copilot-spaces.click', {target: 'ADD_GITHUB_FILES_BUTTON'})
    setShowMultiFilePicker(true)
  }

  const openRepoSelectPanel = () => {
    sendEvent('copilot-spaces.click', {target: 'ADD_REPOSITORY_BUTTON'})
    setRepoSelectError(undefined)
    setShowRepoSelectPanel(true)
  }

  const openGitHubUrlDialog = () => {
    sendEvent('copilot-spaces.click', {target: 'ADD_GITHUB_URL_BUTTON'})
    setShowAddGitHubUrlDialog(true)
  }

  const openTextFileDialog = () => {
    sendEvent('copilot-spaces.click', {target: 'ADD_TEXT_FILE_BUTTON'})
    setShowAddTextFileDialog(true)
  }

  const handleRepoSelected = useCallback(
    async (repoId: number) => {
      if (!repoId) return

      selectingRepoRef.current = true
      setRepoSelectError(undefined)

      try {
        const repo = await fetchRepo(repoId)
        const repositoryResource: CopilotSpaceRepositoryResource = {
          type: 'repository',
          id: `${repo.id}`,
          repositoryId: repo.id,
          nwo: `${repo.ownerLogin}/${repo.name}`,
          ownerAvatarUrl: repo.ownerAvatarUrl,
          ownerType: repo.ownerType,
          markedForDestroy: false,
        }

        onResourcesAdded([repositoryResource])
        sendEvent('copilot-spaces.click', {target: 'SAVED_REPOSITORY_FROM_REPO_PICKER'})
        onRepoSelect?.(repo)
        setShowRepoSelectPanel(false)
      } catch (error) {
        const message = error instanceof Error ? error.message : 'An unknown error occurred'
        setRepoSelectError('Error: failed to attach repository. Please try again.')
        onError?.([message])
      } finally {
        selectingRepoRef.current = false
      }
    },
    [onError, onRepoSelect, onResourcesAdded],
  )

  return (
    <>
      <FileUploadInput
        ref={fileUploadInputRef}
        onResourcesAdded={onResourcesAdded}
        onError={onError}
        onUploadingChange={onUploadingChange}
      />
      <ActionMenu anchorRef={attachmentButtonRef}>
        <ActionMenu.Anchor ref={attachmentButtonRef}>{children}</ActionMenu.Anchor>
        <ActionMenu.Overlay align="end" width="small">
          <ActionList>
            <ActionList.Group>
              <ActionList.GroupHeading>Repository</ActionList.GroupHeading>
              <ActionList.Item onSelect={openRepoSelectPanel}>
                <ActionList.LeadingVisual>
                  <RepoIcon {...testIdProps('add-repository-menu-item-icon')} />
                </ActionList.LeadingVisual>
                Add repository
              </ActionList.Item>
              <ActionList.Item onSelect={openFilePicker}>
                <ActionList.LeadingVisual>
                  <FileCodeIcon {...testIdProps('add-github-files-menu-item-icon')} />
                </ActionList.LeadingVisual>
                Add files from repository
              </ActionList.Item>
              <ActionList.Item onSelect={openGitHubUrlDialog}>
                <ActionList.LeadingVisual>
                  <LinkIcon />
                </ActionList.LeadingVisual>
                Link files, pull requests, and issues
              </ActionList.Item>
            </ActionList.Group>

            <ActionList.Divider />

            <ActionList.Group>
              <ActionList.GroupHeading>Local</ActionList.GroupHeading>
              <ActionList.Item
                onSelect={() => {
                  sendEvent('copilot-spaces.click', {target: 'LOCAL_FILE_UPLOAD'})
                  fileUploadInputRef.current?.addResources()
                }}
              >
                <ActionList.LeadingVisual>
                  <UploadIcon />
                </ActionList.LeadingVisual>
                Upload a file
              </ActionList.Item>
              <ActionList.Item onSelect={openTextFileDialog}>
                <ActionList.LeadingVisual>
                  <PasteIcon {...testIdProps('add-text-file-menu-item-icon')} />
                </ActionList.LeadingVisual>
                Add text content
              </ActionList.Item>
            </ActionList.Group>
          </ActionList>
        </ActionMenu.Overlay>
      </ActionMenu>
      {showAddTextFileDialog && (
        <TextFileDialog
          sizePercentage={sizePercentage}
          onClose={() => setShowAddTextFileDialog(false)}
          onSave={resource => onResourcesAdded([resource])}
        />
      )}
      {showMultiFilePicker && (
        <SpacesFilePicker
          isOpen={showMultiFilePicker}
          onClose={() => setShowMultiFilePicker(false)}
          onResourcesAdded={onResourcesAdded}
          owner={owner}
          resourceCounts={resourceCounts}
          initialRepo={initialRepo}
          onRepoSelect={onRepoSelect}
        />
      )}
      {showAddGitHubUrlDialog && (
        <GitHubUrlDialog
          onClose={() => setShowAddGitHubUrlDialog(false)}
          onSave={(resources: CopilotSpaceResource[]) => onResourcesAdded(resources)}
          resourcesToSave={resourcesToSave}
          resourceCounts={resourceCounts}
          owner={owner}
          spaceOwner={spaceOwner}
        />
      )}
      {showRepoSelectPanel && (
        <RepoSelectPanel
          cancelReturnFocusRef={attachmentButtonRef}
          submitReturnFocusRef={attachmentButtonRef}
          open={showRepoSelectPanel}
          onOpenChange={newOpen => {
            // Do not allow panel to open/close due to focus changes while selecting a repo is still in progress
            if (!selectingRepoRef.current) setShowRepoSelectPanel(newOpen)
          }}
          selectedRepoIds={new Set<number>()}
          onSelectRepo={handleRepoSelected}
          selectionVariant="instant"
          description="Select a repository to add to this space."
          error={repoSelectError}
          ownerDisplayLogin={owner}
          includeForks
        />
      )}
    </>
  )
}
